// GET /api/business/v1/business/features — the TENANT-EFFECTIVE feature set
// from ihjizli-bff-business: per key, the final answer for the active business
// (X-Business-Id) after admin kill switch × admin per-tenant allow/deny × plan
// × the owner's own switch. Shape:
//   { business_id, plan_code, plan_known,
//     features: { "erp.accounts": true, "erp.inventory": false, … },
//     module_plans: { free: [], standard: ["accounts","print"], … },
//     items: [{ key, label, group, enabled, source, … }] }
//
// Global-only keys (ui.*, platform.*) are not in `features`; they keep coming
// from GET /v1/config (lib/config.ts). Online, the module gates read
// mergeFlags(config, features): features win key by key. Node mode never asks
// (its flags ride on /me from the licence).
//
// FAIL CLOSED: while the features are missing (load failed) every `erp.*`
// key reads OFF — the config's own erp.* values are not trusted as the
// tenant's answer.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { businessApi } from './api';

/**
 * Which layer decided a row's `enabled` (platform internal/features Source):
 * global_off = admin kill switch · admin = per-tenant allow/deny · owner_off =
 * the owner's own switch · plan = the plan includes/excludes it · plan_unknown
 * = no plan on record · default = the registry default.
 */
export type FeatureSource = 'global_off' | 'admin' | 'owner_off' | 'plan' | 'plan_unknown' | 'default';

/** One row of `items` (bff-business FeatureItem, snake_case as on the wire). */
export interface FeatureItem {
  key: string;
  label: string;
  group: string;
  enabled: boolean;
  /** Which layer decided (a FeatureSource; kept open for values a newer server may add). */
  source: FeatureSource | string;
  global: boolean;
  admin_enabled: boolean | null;
  owner_enabled: boolean | null;
  plan_includes: boolean;
  owner_toggle: boolean;
  default_on: boolean;
  admin_reason: string;
  admin_set_by: string;
  admin_set_at: string | null;
  owner_set_by: string;
  owner_set_at: string | null;
}

export interface BusinessFeatures {
  businessId: string;
  planCode: string;
  planKnown: boolean;
  /**
   * The server's answer per key — true AND false are kept (a false overrides
   * a config true in mergeFlags); non-boolean values are dropped.
   */
  features: Record<string, boolean>;
  modulePlans: Record<string, string[]>;
  items: FeatureItem[];
}

/** The tenant-scoped namespace the features endpoint is authoritative for. */
const TENANT_PREFIX = 'erp.';

function featuresObject(raw: unknown): Record<string, unknown> | null {
  const f = raw && typeof raw === 'object' ? (raw as { features?: unknown }).features : undefined;
  return f && typeof f === 'object' && !Array.isArray(f) ? (f as Record<string, unknown>) : null;
}

/** Every literal boolean in the payload's `features` (true and false). */
export function resolvedFeatures(raw: unknown): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  const f = featuresObject(raw);
  if (!f) return out;
  for (const [k, v] of Object.entries(f)) if (typeof v === 'boolean') out[k] = v;
  return out;
}

/** The payload's `features` as a map that only contains real `true`s (like normalizeFlags). */
export function normalizeFeatures(raw: unknown): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(resolvedFeatures(raw))) if (v) out[k] = true;
  return out;
}

/**
 * Online flags = config overlaid with the tenant features (features win).
 * `features` undefined (not loaded / failed) → every erp.* key is OFF.
 * The result holds only real trues.
 */
export function mergeFlags(
  configFlags: Record<string, boolean> | null | undefined,
  features: Record<string, boolean> | null | undefined,
): Record<string, boolean> {
  const merged: Record<string, boolean> = { ...(configFlags ?? {}), ...(features ?? {}) };
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(merged)) {
    if (v !== true) continue;
    if (!features && k.startsWith(TENANT_PREFIX)) continue;
    out[k] = true;
  }
  return out;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function modulePlansOf(raw: unknown): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [plan, mods] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(mods)) out[plan] = mods.filter((m): m is string => typeof m === 'string');
  }
  return out;
}

function itemsOf(raw: unknown): FeatureItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((i): i is FeatureItem => !!i && typeof i === 'object' && typeof (i as { key?: unknown }).key === 'string');
}

export const FEATURES_QUERY_KEY = ['business', 'features'] as const;

export async function fetchFeatures(): Promise<BusinessFeatures> {
  const raw = await businessApi.get<unknown>('/v1/business/features');
  const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    businessId: str(obj.business_id),
    planCode: str(obj.plan_code),
    planKnown: obj.plan_known === true,
    features: resolvedFeatures(raw),
    modulePlans: modulePlansOf(obj.module_plans),
    items: itemsOf(obj.items),
  };
}

export function useFeaturesQuery(enabled = true) {
  return useQuery({ queryKey: FEATURES_QUERY_KEY, queryFn: fetchFeatures, enabled, staleTime: 30_000, retry: 1 });
}

// ---------------------------------------------------------------------------
// The owner's switch — settings › «الميزات».

/** The caption per source — the business app's copy, word for word. */
export const SOURCE_LABEL: Record<FeatureSource, string> = {
  global_off: 'مطفأة على الكل',
  admin: 'قرار الإدارة',
  owner_off: 'أطفأتَها',
  plan: 'بالخطة',
  plan_unknown: 'الخطة غير معروفة',
  default: 'الافتراضي',
};

/** The caption for a wire `source`; a value this build does not know is shown as-is, never blank. */
export function sourceLabel(source: string): string {
  return (SOURCE_LABEL as Record<string, string>)[source] ?? source;
}

/**
 * May the owner switch this row? Only a key the registry marks owner-switchable,
 * and only when the layers above the owner allow it: the row is on (so the owner
 * may turn it off) or the owner is the one who turned it off (so they may turn
 * it back on). Off by the plan, by admin or by the kill switch → not the owner's.
 */
export function ownerCanToggle(item: Pick<FeatureItem, 'owner_toggle' | 'source' | 'enabled'>): boolean {
  return item.owner_toggle && (item.source === 'owner_off' || item.enabled);
}

/** PUT /api/business/v1/business/features/{key} {enabled} (owner only) → the row as the platform now resolves it. */
export function setOwnerFeature(key: string, enabled: boolean): Promise<FeatureItem> {
  return businessApi.put<FeatureItem>(`/v1/business/features/${encodeURIComponent(key)}`, { enabled });
}

export interface OwnerFeatureChange {
  key: string;
  enabled: boolean;
}

/**
 * The owner's switch as a mutation. On success the answered row replaces its
 * item (and its `features` value) at once, then FEATURES_QUERY_KEY is
 * invalidated — the shell's module gates read that same query, so the rail
 * and the settings menu follow the write.
 */
export function useSetOwnerFeature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, enabled }: OwnerFeatureChange) => setOwnerFeature(key, enabled),
    onSuccess: (item) => {
      qc.setQueryData<BusinessFeatures>(FEATURES_QUERY_KEY, (old) =>
        old
          ? {
              ...old,
              features: { ...old.features, [item.key]: item.enabled },
              items: old.items.map((i) => (i.key === item.key ? item : i)),
            }
          : old,
      );
      void qc.invalidateQueries({ queryKey: FEATURES_QUERY_KEY });
    },
  });
}
